import { FieldGroup, FormLines, LoadingSkeleton, StaticField, useComputed, useTestIds } from "@homebound/beam";
import { type QueryResult } from "src/apolloHooks";
import { Price } from "src/components/Price";
import { ChangeEventType } from "src/generated/graphqlTypes";
import { useCurrentUser } from "src/hooks/useCurrentUser";
import { WorkflowFormState } from "src/routes/projects/change-events/guided/workflows/types";
import { count, queryResult, signedCount } from "src/utils";
import {
  ReviewProjectImpact_ProjectCostCodeFragment,
  ReviewProjectScheduleImpactQuery,
  ReviewProjectScheduleImpactQueryVariables,
  useReviewProjectImpactComputeCostQuery,
  useReviewProjectScheduleImpactQuery,
} from "../3.ReviewImpactStep.generated";
import { ApplyConfigurationFormInput } from "../applyConfigurationForm";
import { createProjectBudgetRows, reviewImpactBudgetTotals } from "../reviewImpactUtils";

type ApplyConfigurationSummaryStatsProps = {
  formState: WorkflowFormState<ApplyConfigurationFormInput>;
  /** What this project's lot type calls its client, i.e. "Homeowner", for the Chargeable label. */
  clientNoun: string;
  projectCostCodes: ReviewProjectImpact_ProjectCostCodeFragment[];
  readyPlanId: string;
  readyPlanOptionIds: string[];
  versionId: string | undefined;
  scheduleImpactVariables: ReviewProjectScheduleImpactQueryVariables;
};

/**
 * The summary of what Apply will do, for a project with no signed contract.
 *
 * There is no change event to read the impact off of, so the numbers are computed from the selected configuration,
 * exactly as the Review Impact step does it.
 */
export function ApplyConfigurationSummaryStats(props: ApplyConfigurationSummaryStatsProps) {
  const { formState, clientNoun, projectCostCodes, readyPlanId, readyPlanOptionIds } = props;
  const { versionId, scheduleImpactVariables } = props;
  const isChargeable = useComputed(() => formState.type.value === ChangeEventType.External, [formState]);
  const currentUser = useCurrentUser();
  const tid = useTestIds(props, "summaryStats");

  const costQuery = useReviewProjectImpactComputeCostQuery({
    variables: { readyPlanId, readyPlanOptionIds, versionId },
  });
  /**
   * Deliberately not gating the panel: projecting the schedule re-runs the simulator, so every other stat here would
   * sit behind a 30s-timeout HTTP call for a number it doesn't need. Only its own field waits on it.
   */
  const scheduleImpactQuery = useReviewProjectScheduleImpactQuery({ variables: scheduleImpactVariables });

  return queryResult(costQuery, ({ computeReadyPlanCost }) => {
    const { protoProjectItems } = computeReadyPlanCost;
    // The initial workflow replaces the whole configuration, so the computed items *are* the new scope
    const rows = createProjectBudgetRows(
      projectCostCodes,
      protoProjectItems.map((ppi) => ({
        costCode: ppi.item.costCode,
        totalCostInCents: ppi.totalCostInCents,
        // `computeReadyPlanCost` only prices the trade cost, so there is no price to roll up
        totalPriceInCents: undefined,
      })),
      true,
    );
    const { currentBudgetInCents, newBudgetInCents } = reviewImpactBudgetTotals(projectCostCodes, rows);

    return (
      <FormLines labelStyle="left" width="full" compact>
        <FieldGroup>
          <StaticField label={`Chargeable to ${clientNoun}`} value={isChargeable ? "Yes" : "No"} {...tid.chargeable} />
          <StaticField label="Scope Impact" value={count(protoProjectItems.length, "item")} {...tid.scopeImpact} />
        </FieldGroup>
        <FieldGroup>
          <StaticField label="Current Budget" {...tid.currentBudget}>
            <Price valueInCents={currentBudgetInCents} />
          </StaticField>
          <StaticField label="Schedule Impact" {...tid.scheduleImpact}>
            <ScheduleImpactValue query={scheduleImpactQuery} />
          </StaticField>
        </FieldGroup>
        <FieldGroup>
          <StaticField label="New Budget" {...tid.newBudget}>
            <Price valueInCents={newBudgetInCents} />
          </StaticField>
          <StaticField label="Created by" value={currentUser.name} {...tid.createdBy} />
        </FieldGroup>
      </FormLines>
    );
  });
}

type ScheduleImpactValueProps = {
  query: QueryResult<ReviewProjectScheduleImpactQuery, ReviewProjectScheduleImpactQueryVariables>;
};

/** The schedule stat on its own, since the simulation lands well after the cost numbers do. */
function ScheduleImpactValue(props: ScheduleImpactValueProps) {
  const { query } = props;
  if (query.loading) return <LoadingSkeleton rows={1} columns={1} size="sm" />;
  if (query.error || !query.data) return <>Unavailable</>;
  // Null when the schedule has no completion date to measure a difference from
  const { differenceInDays } = query.data.projectScheduleImpact;
  return <>{differenceInDays ? signedCount(differenceInDays, "day") : "None"}</>;
}
